import { useEffect, useCallback } from "react";
import { useI18n, translations } from "./i18n";

/**
 * Dynamic SEO updates per language.
 *
 * og:title, og:description and twitter:* tags are kept STATIC in /public/index.html
 * so social crawlers always show the Portuguese share preview. This component
 * updates document.title, description, keywords, lang attribute and JSON-LD
 * per selected language.
 */
const Seo = () => {
  const { lang } = useI18n();

  const setMeta = useCallback((name, content, attr = "name") => {
    let el = document.head.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, name);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  }, []);

  useEffect(() => {
    const isPt = lang === "pt";

    const desc = isPt
      ? "Crédito habitação sem custos para si: comparo propostas de vários bancos e acompanho o processo até à escritura. Resposta em 24h. Quarteira · Algarve · todo o país. Intermediário de crédito autorizado pelo Banco de Portugal (reg. 0006056)."
      : "Mortgage in Portugal at no cost to you: I compare proposals from several banks and handle the whole process to completion. Reply within 24h. Quarteira · Algarve · nationwide. Credit intermediary authorised by the Bank of Portugal (reg. 0006056).";

    document.title = isPt
      ? "Crédito Habitação 2026 — Compare 10 Bancos | Diogo Almeida"
      : "Mortgage in Portugal 2026 — Compare 10 Banks | Diogo Almeida";

    const keywords = isPt
      ? "crédito habitação, simulador crédito habitação, melhor taxa crédito habitação, crédito habitação 2026, transferência de crédito habitação, renegociação crédito habitação, spread crédito habitação, Euribor, Euribor 12M, DSTI 45%, taxa de esforço, LTV não residente, taxa fixa crédito habitação, taxa variável, decisão prévia, intermediário de crédito, gestor de crédito, mediador de crédito, crédito habitação Algarve, crédito habitação Faro, crédito habitação Quarteira, crédito habitação Lisboa, crédito habitação Porto, crédito habitação não residentes, crédito habitação estrangeiros, equity release, consolidação de créditos, seguro de vida crédito habitação, seguro de saúde"
      : "mortgage Portugal, mortgage Portugal 2026, mortgage broker Portugal, mortgage broker Algarve, mortgage rates Portugal, mortgage Algarve, mortgage Faro, mortgage Quarteira, mortgage Lisbon, mortgage for foreigners Portugal, non-resident mortgage Portugal, non-resident LTV Portugal, expat mortgage Portugal, mortgage pre-approval Portugal, DSTI 45%, Euribor 12M spread, fixed rate mortgage Portugal, refinance mortgage Portugal, mortgage transfer Portugal, equity release Portugal, life insurance Portugal, health insurance Portugal, home insurance Portugal";

    setMeta("description", desc);
    setMeta("keywords", keywords);
    document.documentElement.lang = isPt ? "pt-PT" : "en";
    const canon = document.head.querySelector('link[rel="canonical"]');
    if (canon) canon.setAttribute("href", isPt ? "https://diogoalmeida.pt" : "https://diogoalmeida.pt/en");

    // JSON-LD structured data (search-engine only)
    let ld = document.getElementById("ld-financial");
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = "ld-financial";
      document.head.appendChild(ld);
    }
    const offers = isPt
      ? [
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Crédito Habitação" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Transferência de Crédito Habitação" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Renegociação de Crédito Habitação" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Crédito Habitação para Não-Residentes" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Equity Release" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Seguro de Vida" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Seguro de Saúde" } },
        ]
      : [
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Mortgage in Portugal" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Mortgage Transfer Portugal" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Mortgage Renegotiation Portugal" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Non-Resident Mortgage Portugal" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Equity Release Portugal" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Life Insurance Portugal" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Health Insurance Portugal" } },
        ];

    ld.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "FinancialService",
        name: "Diogo Almeida",
        legalName: "Decisão Ordenada, Lda",
        description: desc,
        areaServed: ["Portugal", "Algarve", "Quarteira", "Faro", "Lisboa", "Porto"],
        url: "https://diogoalmeida.pt",
        telephone: "+351965405031",
        email: "diogoalmeida@dsicredito.pt",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Proa Mar Building, Av. de Ceuta nº 3 Store B",
          postalCode: "8125-507",
          addressLocality: "Quarteira",
          addressRegion: "Faro",
          addressCountry: "PT",
        },
        geo: { "@type": "GeoCoordinates", latitude: 37.0688357, longitude: -8.0993321 },
        hasMap: "https://maps.app.goo.gl/WJTWhJ6n239xTXvv5",
        priceRange: "Free advisory",
        identifier: { "@type": "PropertyValue", name: "Banco de Portugal", value: "0006056" },
        sameAs: [
          "https://www.facebook.com/diogoalmeidaDS",
          "https://www.instagram.com/diogoalmeidads",
          "https://www.linkedin.com/in/diogoalmeidads",
          "https://maps.app.goo.gl/WJTWhJ6n239xTXvv5",
        ],
        makesOffer: offers,
      },
      {
        "@context": "https://schema.org",
        "@type": "Person",
        name: "Diogo Almeida",
        jobTitle: isPt ? "Gestor de Crédito · Intermediário de Crédito Vinculado" : "Mortgage Advisor · Tied Credit Intermediary",
        worksFor: {
          "@type": "Organization",
          name: "Decisão Ordenada, Lda",
          url: "https://diogoalmeida.pt",
        },
        url: "https://diogoalmeida.pt",
        telephone: "+351965405031",
        email: "diogoalmeida@dsicredito.pt",
        areaServed: "Portugal",
        sameAs: [
          "https://www.facebook.com/diogoalmeidaDS",
          "https://www.instagram.com/diogoalmeidads",
          "https://www.linkedin.com/in/diogoalmeidads",
          "https://maps.app.goo.gl/WJTWhJ6n239xTXvv5",
        ],
      },
    ]);

    // FAQPage structured data — eligible for rich results (boosts CTR)
    let faqLd = document.getElementById("ld-faq");
    if (!faqLd) {
      faqLd = document.createElement("script");
      faqLd.type = "application/ld+json";
      faqLd.id = "ld-faq";
      document.head.appendChild(faqLd);
    }
    const faqItems = translations[lang]?.faq?.items || [];
    faqLd.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      })),
    });
  }, [lang, setMeta]);

  return null;
};

export default Seo;
